/**
 * Native iPhone capabilities (via Capacitor).
 *
 * Every export is a safe no-op in the browser, so feature code can call them
 * unconditionally and the website behaves exactly as before. Plugins are loaded
 * with dynamic import() so they only run inside the native app.
 */
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();

/** One-time setup when the native app starts. */
export function initNative() {
  if (!isNative) return;
  document.documentElement.classList.add('cap-native');
}

/**
 * Photograph a document (bank statement, ID, voided check...) with the camera, or
 * pick one from the photo library, and return it as an uploadable File.
 * Returns null if the user cancels or on the web.
 */
export async function scanDocument() {
  if (!isNative) return null;
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  try {
    const photo = await Camera.getPhoto({
      source: CameraSource.Prompt,
      resultType: CameraResultType.Uri,
      quality: 85,
      correctOrientation: true,
      saveToGallery: false,
      promptLabelHeader: 'Add a document',
      promptLabelPhoto: 'Choose from Photos',
      promptLabelPicture: 'Scan with Camera',
    });
    if (!photo.webPath) return null;
    const blob = await (await fetch(photo.webPath)).blob();
    const ext = (photo.format || 'jpeg').toLowerCase() === 'png' ? 'png' : 'jpg';
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    return new File([blob], `scan-${stamp}.${ext}`, { type: ext === 'png' ? 'image/png' : 'image/jpeg' });
  } catch (e) {
    // Cancelling the camera or picker is not an error.
    if (/cancel/i.test(String(e && e.message))) return null;
    throw e;
  }
}

/** Open the iOS share sheet. Returns false on the web so callers can fall back. */
export async function shareNative({ title, text, url }) {
  if (!isNative) return false;
  const { Share } = await import('@capacitor/share');
  try {
    await Share.share({ title, text, url, dialogTitle: title });
  } catch {
    /* user dismissed the sheet */
  }
  return true;
}

/** Light tap, or a success / warning / error notification buzz. */
export async function haptic(kind = 'light') {
  if (!isNative) return;
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
    if (kind === 'light') {
      await Haptics.impact({ style: ImpactStyle.Light });
    } else {
      const type = kind === 'success' ? NotificationType.Success
        : kind === 'error' ? NotificationType.Error
        : NotificationType.Warning;
      await Haptics.notification({ type });
    }
  } catch {
    /* haptics are best-effort */
  }
}
